import { BusinessRuleType, EntityDefinition } from '../types/entity-definition.interface';
import { ParameterTypes } from '../types/formula.interface';
import { FREQUENCY_OPTIONS, AutomaticFrequency, AutomaticMeasure } from '../types/measure.interface';
import { isMeasureNameUniqueAcrossEntities } from '../utils/measure-name-uniqueness.util';

export const AUTOMATIC_ALLOWED_PARAMETER_TYPES: readonly string[] = [
  ParameterTypes.ACQUISITION,
  ParameterTypes.CONSTANT,
  ParameterTypes.MEASURE,
];

export const FORBIDDEN_AUTOMATIC_EXTRA_TYPES = [ParameterTypes.INPUT, ParameterTypes.ACQUISITION] as const;

const WEEKLY = AutomaticFrequency.OneWeek;
const MONTHLY = AutomaticFrequency.OneMonth;
const LONG_PERIODS = new Set<AutomaticFrequency>([
  AutomaticFrequency.FourMonths,
  AutomaticFrequency.SixMonths,
  AutomaticFrequency.OneYear,
]);

const DAY_OF_WEEK_OPTIONS = [
  { id: 1, name: 'common.weekdays.monday' },
  { id: 2, name: 'common.weekdays.tuesday' },
  { id: 3, name: 'common.weekdays.wednesday' },
  { id: 4, name: 'common.weekdays.thursday' },
  { id: 5, name: 'common.weekdays.friday' },
  { id: 6, name: 'common.weekdays.saturday' },
  { id: 7, name: 'common.weekdays.sunday' },
];
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ id: i + 1, name: String(i + 1) }));
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ id: i + 1, name: `common.months.${i + 1}` }));

const requiresDayOfMonth = (freq: AutomaticFrequency | null): boolean => freq === MONTHLY || (freq != null && LONG_PERIODS.has(freq));
const requiresMonth = (freq: AutomaticFrequency | null): boolean => freq != null && LONG_PERIODS.has(freq);
const lockedAfterFrequency = (entity: Record<string, unknown> | null): boolean => entity?.['frequency'] != null;

export const automaticMeasureDefinition: EntityDefinition<AutomaticMeasure> = {
  entityType: 'AutomaticMeasure',
  i18nBase: 'entities.automaticMeasure',
  list: {
    columns: [{ field: 'name', headerKey: 'common.labels.name', sortable: true, filterable: true }],
  },
  fields: [
    { field: 'name', labelKey: 'common.labels.name', required: true, type: 'text', minLength: 3, maxLength: 50 },
    {
      field: 'frequency',
      labelKey: 'entities.automaticMeasure.fields.frequency',
      required: true,
      type: 'select',
      values: FREQUENCY_OPTIONS,
      filterable: false,
      placeholderKey: 'common.placeholders.selectFrequency',
      disabled: lockedAfterFrequency,
      rowGroup: 'schedule',
    },
    {
      field: 'hour',
      labelKey: 'entities.automaticMeasure.fields.hour',
      required: true,
      type: 'hour',
      condition: v => v['frequency'] != null,
      disabled: lockedAfterFrequency,
      rowGroup: 'schedule',
    },
    {
      field: 'dayOfWeek',
      labelKey: 'entities.automaticMeasure.fields.dayOfWeek',
      required: true,
      type: 'select',
      values: DAY_OF_WEEK_OPTIONS,
      filterable: false,
      placeholderKey: 'common.placeholders.selectWeekday',
      condition: v => v['frequency'] === WEEKLY,
      disabled: lockedAfterFrequency,
    },
    {
      field: 'dayOfMonth',
      labelKey: 'entities.automaticMeasure.fields.dayOfMonth',
      required: true,
      type: 'select',
      values: DAY_OF_MONTH_OPTIONS,
      filterable: false,
      placeholderKey: 'common.placeholders.selectDayOfMonth',
      condition: v => requiresDayOfMonth(v['frequency'] as AutomaticFrequency | null),
      rowGroup: 'anchor',
      disabled: lockedAfterFrequency,
    },
    {
      field: 'month',
      labelKey: 'entities.automaticMeasure.fields.month',
      required: true,
      type: 'select',
      values: MONTH_OPTIONS,
      filterable: false,
      placeholderKey: 'common.placeholders.selectMonth',
      condition: v => requiresMonth(v['frequency'] as AutomaticFrequency | null),
      rowGroup: 'anchor',
      disabled: lockedAfterFrequency,
    },
    {
      field: 'locationIds',
      labelKey: 'entities.automaticMeasure.fields.locationIds',
      required: false,
      type: 'multi',
      storeType: 'Location',
    },
    {
      field: 'externalId',
      labelKey: 'entities.automaticMeasure.fields.externalId',
      required: false,
      type: 'text',
      minLength: 3,
      maxLength: 50,
    },
    {
      field: 'overwrite',
      labelKey: 'entities.automaticMeasure.fields.overwrite',
      required: false,
      type: 'boolean',
      tooltipKey: 'entities.automaticMeasure.tooltips.overwrite',
    },
    { field: 'description', labelKey: 'common.labels.description', required: false, type: 'textarea' },
    { field: 'assetId', labelKey: 'entities.automaticMeasure.fields.assetId', type: 'hidden', required: true },
  ],
  validationRules: [
    {
      type: BusinessRuleType.EDIT_VALIDATION,
      rule: (entity: AutomaticMeasure, store) => (store ? isMeasureNameUniqueAcrossEntities(entity, store, 'AutomaticMeasure') : true),
      errorMessageKey: 'entities.automaticMeasure.messages.duplicateError',
    },
  ],
  actionButtons: {
    parent: {
      add: true,
    },
  },
  formOptions: {
    showDeleteButton: false,
  },
  messages: {
    dialogTitles: {
      create: () => 'entities.automaticMeasure.messages.createTitle',
      edit: () => 'entities.automaticMeasure.messages.editTitleWithName',
      delete: () => 'common.confirmations.deleteTitleWithName',
    },
    buttons: {
      add: () => 'entities.automaticMeasure.messages.addButton',
      create: () => 'entities.automaticMeasure.messages.addButton',
      edit: () => 'entities.automaticMeasure.messages.editTitleWithName',
      delete: () => 'entities.automaticMeasure.messages.deleteTitle',
    },
    confirmDelete: () => 'entities.automaticMeasure.messages.deleteConfirm',
  },
  enableFiltering: true,
};
